'use client';

import { Combobox } from '@systemeio/components';
import { ChevronDownIcon } from '@systemeio/icons';
import { useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useFormControlSize } from '../form-control-size-context';
import { cancelBackspaceClear, getCreatableQuery, getSelectedOption, isCreateOption, makeCreateOption, OptionsSkeleton, shouldVirtualize, type CreateOption, type FormSelectDataType, type FormSelectIdType, type FormSelectProps } from './form-select';
interface FormSelectWithCreateProps<T extends FormSelectIdType> extends FormSelectProps<T> {
  createData: {
    getCustomCaption: (value: string) => string;
    onCreate: (data: string) => Promise<void>;
  };
}
function FormSelectWithCreate<T extends FormSelectIdType>({
  ariaLabel,
  createData,
  data,
  dataTest,
  description,
  disabled,
  error,
  inputRef,
  isPreFetching,
  label,
  onChange,
  onClose,
  onOpen,
  placeholder,
  required,
  size,
  staticData,
  value,
  withoutCloseIcon
}: FormSelectWithCreateProps<T>) {
  const inheritedSize = useFormControlSize();
  const {
    t
  } = useLocoTranslation();
  // Mirrored, not controlled: Base UI owns the input so it can derive the desktop label
  // from the selection, and the mobile drawer search opens empty.
  const [searchValue, setSearchValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const selectedOption = getSelectedOption(value, data ?? staticData);
  const isDisabled = disabled || isPreFetching || isCreating || value !== undefined && !data && !staticData;
  const resolvedPlaceholder = placeholder ?? t('global.select');
  const accessibleLabel = ariaLabel ?? (typeof label === 'string' ? label : resolvedPlaceholder);
  const creatableQuery = getCreatableQuery(searchValue, data);
  const onCreate = async (caption: string) => {
    setIsCreating(true);
    try {
      await createData.onCreate(caption);
      setSearchValue('');
    } finally {
      setIsCreating(false);
    }
  };
  return <div aria-busy={isPreFetching || isCreating || undefined} className="grid w-full content-start gap-2">
      <Combobox.Root<FormSelectDataType<T> | CreateOption> aria-label={accessibleLabel} disabled={isDisabled} drawerTitle={label ?? resolvedPlaceholder} isItemEqualToValue={(option, selected) => Object.is(option.id, selected.id)} itemToStringLabel={option_0 => option_0.caption} itemToStringValue={option_1 => String(option_1.id)} items={data ?? []} onOpenChange={open => {
      if (open) onOpen?.();else onClose?.();
    }} onSearchChange={setSearchValue} onValueChange={(option_2, eventDetails) => {
      if (cancelBackspaceClear(withoutCloseIcon, eventDetails)) return;
      if (option_2 && isCreateOption(option_2)) {
        void onCreate(option_2.caption);
        return;
      }
      onChange(option_2?.id as T | undefined);
    }} placeholder={resolvedPlaceholder} required={required} size={size ?? inheritedSize} value={selectedOption} virtualized={shouldVirtualize(data)}>
        {label !== undefined ? <Combobox.InputLabel required={required}>{label}</Combobox.InputLabel> : null}
        <Combobox.InputGroup aria-label={label === undefined ? accessibleLabel : undefined} className={isPreFetching || isCreating ? 'animate-pulse' : undefined} data-invalid={error ? '' : undefined}>
          <Combobox.Input {...dataTest} ref={inputRef} />
          {!withoutCloseIcon && selectedOption && !isDisabled ? <Combobox.Clear aria-label={t('global.clear')} /> : null}
          <Combobox.Trigger aria-label={accessibleLabel}>
            <ChevronDownIcon />
          </Combobox.Trigger>
        </Combobox.InputGroup>
        <Combobox.Content>
          {data === undefined ? <OptionsSkeleton /> : <>
              <Combobox.Empty description={t('dashboard.droplist.default_description')}>
                {t('dashboard.droplist.default_title')}
              </Combobox.Empty>
              <Combobox.List>
                {(option_3: FormSelectDataType<T>) => <Combobox.Item data-test-id={option_3.dataTestAttribute} disabled={option_3.disabled} key={String(option_3.id)} value={option_3}>
                    <Combobox.ItemText>{option_3.customCaption ?? option_3.caption}</Combobox.ItemText>
                    {option_3.additionalRender}
                    <Combobox.ItemIndicator />
                  </Combobox.Item>}
              </Combobox.List>
            </>}
          {creatableQuery ? <Combobox.CreateOption value={makeCreateOption(creatableQuery)}>
              {createData.getCustomCaption(creatableQuery)}
            </Combobox.CreateOption> : null}
        </Combobox.Content>
      </Combobox.Root>
      {description ? <Combobox.Description>{description}</Combobox.Description> : null}
      {error ? <Combobox.Error>{error}</Combobox.Error> : null}
    </div>;
}
export { FormSelectWithCreate };
export type { FormSelectWithCreateProps };